import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Collapse, TextField } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { AppSnackbar, ConfigFieldControl, CoreEvent } from '@sierralabs/core-ui';
import { useTaskEntityData, useTaskFields } from '@sierralabs/workflow-ui';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useFindNearestComponent } from '../../hooks/site-route/useFindNearestComponent';
import SlotRenderer from '../renderers/SlotRenderer';
import { SiteComponent } from '../SiteComponentPlugin';
const StyledAlert = styled(Alert) `
  .MuiAlertTitle-root {
    font-size: 15px;
    font-weight: 600;
  }
`;
export var AssignEntityEvent;
(function (AssignEntityEvent) {
    AssignEntityEvent["Assign"] = "assign-entity:assign";
    AssignEntityEvent["OpenDialog"] = "assign-entity:open-dialog";
})(AssignEntityEvent || (AssignEntityEvent = {}));
export function useAssignEntityComponent(component) {
    return useFindNearestComponent(AssignEntityPlugin.type, component);
}
export function useAssignEntityDisabled(assignEntity) {
    var _a, _b;
    const params = useParams();
    // get the dialog from assignEntity component
    const dialog = (_a = assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.children) === null || _a === void 0 ? void 0 : _a.find(c => c.slot === 'dialog');
    const assign = ((_b = dialog === null || dialog === void 0 ? void 0 : dialog.props) === null || _b === void 0 ? void 0 : _b.assign) || {};
    const entityDataId = assign.routeParam
        ? params[assign.routeParam]
        : undefined;
    // get task and check if field is disabled based on property
    const taskEntityData = useTaskEntityData(assign.tableName || '', entityDataId);
    const { task, isLatest } = taskEntityData;
    const { isFieldDisabled } = useTaskFields(task, false);
    // merge relations for disabled field (e.g. task.files)
    const property = (assign.relations || []).join('.');
    const disabled = !isLatest || !!(task && property && isFieldDisabled(property));
    return { assign, disabled, entityDataId, taskEntityData };
}
export const AssignEntityWarning = props => {
    const { component, children } = props;
    const assignEntity = useAssignEntityComponent(component);
    return (React.createElement(Collapse, { in: !assignEntity, style: { minHeight: 'unset' } },
        React.createElement(StyledAlert, { severity: 'warning' },
            React.createElement(AlertTitle, null, "Missing Assign Entity Ancestor"),
            "This component must have an ",
            React.createElement("strong", null, "Assign Entity"),
            " component as its ancestor for it to work properly.",
            children)));
};
export const AssignEntity = props => {
    const component = useSubscribeComponentChanges(AssignEntityPlugin, props);
    const [didUpdate, setDidUpdate] = useState(false);
    const componentId = component.id;
    useEffect(() => {
        return CoreEvent.handle(AssignEntityEvent.Assign, data => (data === null || data === void 0 ? void 0 : data.id) === componentId && setDidUpdate(true));
    }, [componentId]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: AssignEntityPlugin }, props),
        React.createElement(SlotRenderer, { component: component, slot: 'card' }),
        React.createElement(SlotRenderer, { component: component, slot: 'dialog' }),
        React.createElement(AppSnackbar, { open: didUpdate, severity: 'success', onClose: () => setDidUpdate(false) }, "Item(s) assigned successfully.")));
};
export const AssignEntityConfigurator = props => {
    const { component, update } = useConfigComponent(props.component);
    return (React.createElement(ConfigFieldControl, null,
        React.createElement(TextField, { autoFocus: true, fullWidth: true, size: 'small', label: 'Name', value: component.name || '', onChange: event => update('name', event.target.value), InputProps: { disableUnderline: true } })));
};
export const AssignEntityPlugin = {
    name: 'Assign Entity',
    type: 'assign-entity',
    component: AssignEntity,
    configurator: AssignEntityConfigurator,
    slots: [
        { name: 'Assign Entity Card', type: 'assign-entity-card', slot: 'card' },
        {
            name: 'Assign Entity Dialog',
            type: 'assign-entity-dialog',
            slot: 'dialog'
        }
    ]
};
//# sourceMappingURL=AssignEntityPlugin.js.map